import { ref, reactive, computed, inject } from 'vue';
import { streamChat } from './useStream.js';
import { mapResultToState } from '../lib/error-state.js';
import { getSessionId } from './useSession.js';

// Client-side reveal pacing. Whatever the transport does — true token streaming,
// or one buffered burst (e.g. `php artisan serve` doesn't chunk SSE, so the
// browser gets the whole body at once) — we reveal the accumulated text at a
// steady pace so the visitor always sees a typing animation.
const REVEAL_INTERVAL_MS = 16;

/**
 * Owns chat state for the widget:
 *  - messages: list of { role: 'user' | 'assistant', content, streaming?, error? }
 *  - status: 'idle' | 'streaming' | 'error' | 'rate-limited' | 'offline'
 *  - send(text): kicks off a stream
 *
 * Accepts config + event bus as args because Vue's `inject` only sees ancestor
 * provides — so if useAgent is called from the same component that provides
 * the values, it can't read them via inject.
 */
export function useAgent(injectedConfig, injectedEvents) {
    const config = injectedConfig ?? inject('aia.config');
    const events = injectedEvents ?? inject('aia.events');

    const messages = ref([]);
    const status = ref('idle');
    const errorMessage = ref(null);
    let abortController = null;

    const isStreaming = computed(() => status.value === 'streaming');
    const isBusy = computed(() => isStreaming.value);
    const canRetry = computed(() => ['error', 'offline', 'rate-limited'].includes(status.value));
    const isBlocked = computed(() => status.value === 'blocked');

    async function send(text) {
        const trimmed = String(text ?? '').trim();
        if (!trimmed || isBusy.value) return;

        status.value = 'streaming';
        errorMessage.value = null;

        // Append user turn + a placeholder assistant turn we'll stream into.
        // The assistant turn is `reactive` so each streamed delta appended to
        // `content` triggers a re-render (mutating a raw object pushed into the
        // array would only render on the next unrelated reactive change).
        messages.value.push({ role: 'user', content: trimmed });
        const assistantTurn = reactive({ role: 'assistant', content: '', streaming: true });
        messages.value.push(assistantTurn);

        events.emit('message', { role: 'user', content: trimmed });

        abortController = new AbortController();

        // Accumulate the raw stream into `received`; a reveal loop types it into
        // the visible `assistantTurn.content` at a steady pace (accelerating when
        // far behind so long replies don't crawl).
        let received = '';
        let streamDone = false;
        const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        const typewriter = (async () => {
            while (!streamDone || assistantTurn.content.length < received.length) {
                if (assistantTurn.content.length < received.length) {
                    const remaining = received.length - assistantTurn.content.length;
                    const step = Math.max(2, Math.ceil(remaining / 40));
                    assistantTurn.content = received.slice(0, assistantTurn.content.length + step);
                }
                await sleep(REVEAL_INTERVAL_MS);
            }
        })();

        const result = await streamChat({
            endpoint: config.endpoint,
            apiKey: config.apiKey,
            // No `history` is sent: the server keeps the authoritative transcript
            // keyed on session_id. The local `messages` array is for display only.
            body: {
                project_id: config.projectId,
                session_id: getSessionId(),
                locale: config.locale,
                message: trimmed,
            },
            signal: abortController.signal,
            onEvent: (event, data) => {
                if (event === 'message' || event === 'text_delta') {
                    // Prism's SSE adapter emits `text_delta` events with { delta }.
                    const piece = typeof data === 'string' ? data : (data?.delta ?? data?.value ?? '');
                    if (piece) received += piece;
                } else if (event === 'escalation') {
                    events.emit('escalation', data);
                } else if (event === 'tool_call' && data?.tool_name === 'escalate_to_team') {
                    // note_off_topic also arrives as tool_call but stays invisible.
                    events.emit('escalation', data);
                } else if (event === 'tool_result' && data?.result === 'lead_created') {
                    // The lead was actually persisted (not just attempted) — confirm it.
                    events.emit('lead-created', data);
                }
            },
        });

        // Finish revealing whatever streamed in before we settle the turn.
        streamDone = true;
        await typewriter;

        assistantTurn.streaming = false;

        if (!result.ok) {
            const code = result.error?.error?.code ?? null;
            const mapped = mapResultToState({ status: result.status, code });
            status.value = mapped.status;
            assistantTurn.errorKey = mapped.errorKey;
            assistantTurn.error = true;
            if (mapped.status === 'error' && code === 'invalid_key') {
                errorMessage.value = 'auth';
            }
            return;
        }

        // Empty assistant reply usually means we dropped the connection mid-stream.
        if (!assistantTurn.content) {
            status.value = 'offline';
            assistantTurn.error = true;
            assistantTurn.errorKey = 'offline';
            return;
        }

        status.value = 'idle';
        events.emit('message', { role: 'assistant', content: assistantTurn.content });
    }

    function retryLast() {
        const lastUser = [...messages.value].reverse().find((m) => m.role === 'user');
        if (!lastUser) return;
        // Trim the failed assistant turn before resending.
        if (messages.value[messages.value.length - 1]?.role === 'assistant') {
            messages.value.pop();
        }
        // Also remove the user turn so send() can re-add it cleanly.
        messages.value.pop();
        send(lastUser.content);
    }

    function abort() {
        abortController?.abort();
        abortController = null;
    }

    return {
        messages,
        status,
        errorMessage,
        isStreaming,
        isBusy,
        canRetry,
        isBlocked,
        send,
        retryLast,
        abort,
    };
}
